/**
 * Zustand-store rond de battle-engine. Houdt de huidige BattleState + taal vast
 * en biedt acties die de pure engine-functies aanroepen.
 */
import { create } from 'zustand';
import type { Origin } from '../data/types';
import { CHAKRAS, kaartenPoolVoor } from '../data/chakras';
import type { BattleState } from '../engine/state';
import { initBattle } from '../engine/state';
import { eindigBeurt, speelKaart, startGevecht } from '../engine/battle';
import { HOUDING_BONUS, type Houding } from '../data/vn';
import { detecteerTaal, type Taal } from '../i18n';
import { speelTonen, speelFinale } from '../audio';

/** Speelt de mantra-tonen van een actie + de finale bij Theluun-winst. */
function naActieAudio(b: BattleState): void {
  speelTonen(b.tonen);
  if (b.resultaat === 'gewonnen' && b.baasState.baas.verbindingFight) speelFinale();
}

export interface StartOpties {
  chakra: Origin;
  baasIndex: number;
  houding?: Houding;
  /** HP waarmee de speler start (carry tussen sub-bazen); default = vol. */
  startHp?: number;
  /** Voltooide chakra's → welke meesterschapskaarten meereizen. */
  ontgrendeld?: Origin[];
  seed?: number;
}

interface BattleStore {
  battle: BattleState | null;
  taal: Taal;
  context: StartOpties | null;
  zetTaal: (t: Taal) => void;
  start: (opties: StartOpties) => void;
  speel: (instId: string) => void;
  eindBeurt: () => void;
  herstart: () => void;
  stop: () => void;
}

export const useBattleStore = create<BattleStore>((set, get) => ({
  battle: null,
  taal: detecteerTaal(),
  context: null,

  zetTaal: (t) => set({ taal: t }),

  start: (opties) => {
    const seed = opties.seed ?? ((Date.now() & 0x7fffffff) || 1);
    const baas = CHAKRAS[opties.chakra].bazen[opties.baasIndex];
    const pool = kaartenPoolVoor(opties.chakra, new Set(opties.ontgrendeld ?? []));
    const bonus = opties.houding ? HOUDING_BONUS[opties.houding] : {};
    const battle = startGevecht(
      initBattle(opties.chakra, baas, pool, { seed, startHp: opties.startHp, ...bonus })
    );
    set({ battle, context: { ...opties, seed } });
  },

  speel: (instId) => {
    const b = get().battle;
    if (!b || b.resultaat !== 'bezig') return;
    const nb = speelKaart(b, instId);
    set({ battle: nb });
    naActieAudio(nb);
  },

  eindBeurt: () => {
    const b = get().battle;
    if (!b || b.resultaat !== 'bezig') return;
    const nb = eindigBeurt(b);
    set({ battle: nb });
    naActieAudio(nb);
  },

  herstart: () => {
    const ctx = get().context;
    if (!ctx) return;
    get().start({ ...ctx, seed: (Date.now() & 0x7fffffff) || 1 });
  },

  stop: () => set({ battle: null, context: null }),
}));
